import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid2,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { Link as RouterLink } from 'gatsby';
import React from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';

import Autocomplete from '@components/Autocomplete/Autocomplete';
import { ConfirmationDialog } from '@components/ConfirmationDialog';
import ContentLoader from '@components/ContentLoader';
import IconButton from '@components/IconButton';
import NullStateDiv from '@components/NullStateDiv';

type EditorOption = { id: string; name: string; url: string };

type EditorMessages = {
  add: MessageDescriptor;
  remove: MessageDescriptor;
  confirmAdd: MessageDescriptor;
  confirmRemove: MessageDescriptor;
  title: MessageDescriptor;
};

interface EditorProps {
  selected?: EditorOption[];
  options?: EditorOption[];
  loading?: boolean;
  messages: EditorMessages;
  onAdd?: (option: EditorOption) => void | Promise<void>;
  onRemove?: (option: EditorOption) => void | Promise<void>;
  isCreating?: boolean;
}

const Editor: React.FC<EditorProps> = ({
  loading: baseLoading = false,
  options = [],
  messages,
  selected = [],
  onAdd,
  onRemove,
  isCreating = false,
}) => {
  const isAddEnabled = Boolean(onAdd);
  const isRemoveEnabled = Boolean(onRemove);
  const [isMutating, setIsMutating] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(isCreating);

  const [newItem, setNewItem] = React.useState<EditorOption>();

  const loading = baseLoading;

  const { formatMessage } = useIntl();

  const handleAdd = async (option?: EditorOption) => {
    if (!option || isMutating) {
      return;
    }
    setIsMutating(true);
    await onAdd?.(option);
    setIsMutating(false);
  };

  const handleRemove = async (option: EditorOption) => {
    if (isMutating) {
      return;
    }
    setIsMutating(true);
    await onRemove?.(option);
    setIsMutating(false);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setNewItem(undefined);
  };

  const handleEditClick = () => {
    const isCancel = isEditing;
    setIsEditing(!isEditing);
    if (isCancel) {
      stopEditing();
    }
  };

  const filteredOptions = options.filter(
    (option) => !selected.find((selected) => selected.id === option.id)
  );

  return (
    <Card>
      <CardHeader
        title={formatMessage(messages.title)}
        action={
          !isCreating && (
            <IconButton
              name={isEditing ? 'xmark' : 'pencil'}
              iconType="fas"
              onClick={handleEditClick}
            />
          )
        }
      />
      <Divider />
      <CardContent>
        <ContentLoader loading={loading}>
          {isEditing ? (
            <TableContainer>
              <Table padding="checkbox">
                <TableBody>
                  {selected.map((option) => (
                    <TableRow key={option.id}>
                      <TableCell>{option.name}</TableCell>
                      {isRemoveEnabled && (
                        <TableCell align="right">
                          <ConfirmationDialog
                            onConfirm={() => handleRemove(option)}
                            title={`${formatMessage(messages.remove)}?`}
                            content={formatMessage(messages.confirmRemove, {
                              name: option.name,
                            })}
                          >
                            {({ handleOpen }) => (
                              <Button onClick={handleOpen} size="small">
                                {formatMessage(messages.remove)}
                              </Button>
                            )}
                          </ConfirmationDialog>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {isAddEnabled && (
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {formatMessage(messages.add)}
                      </TableCell>
                      <TableCell align="right">
                        <ConfirmationDialog
                          onConfirm={() => handleAdd(newItem)}
                          title={`${formatMessage(messages.add)}?`}
                          content={formatMessage(messages.confirmAdd, {
                            name: newItem?.name,
                          })}
                        >
                          {({ handleOpen }) => {
                            const handleChange = (
                              value: { id: string } | null
                            ) => {
                              const selected = options.find(
                                (o) => o.id === value?.id
                              );
                              if (selected) {
                                setNewItem(selected);
                                if (isCreating) {
                                  handleAdd(selected);
                                } else {
                                  handleOpen();
                                }
                              }
                            };
                            return (
                              <Autocomplete
                                options={filteredOptions}
                                onChange={handleChange}
                              />
                            );
                          }}
                        </ConfirmationDialog>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid2 container spacing={1}>
              {selected.map(({ name, url }, index) => (
                <Grid2 size={{ xs: 12 }} key={`option-${index}`}>
                  <Link component={RouterLink} to={url} color="info.main">
                    {name}
                  </Link>
                </Grid2>
              ))}
              {!selected.length && <NullStateDiv>No data</NullStateDiv>}
            </Grid2>
          )}
        </ContentLoader>
      </CardContent>
    </Card>
  );
};

export default Editor;
export type { EditorMessages, EditorOption };
